import type {
  PostStationConsolidatedStatus,
  PostStationDomainDetailDoctor,
  PostStationDomainDetailResponse,
  PostStationDomainStatus,
  PostStationDomainStatuses,
  PostStationEvaluationItem
} from '@/Network/Types/Responses/PostStationResponses'
import { formatDayMonthInOffset, formatHourMinuteInOffset } from '@/utils/date'

import { DOMAIN_LABELS } from './constants'
import { PostStationStatus } from './types'
import type {
  ComplianceState,
  PostStationCompliance,
  PostStationDomainDetail,
  PostStationDoctor,
  PostStationDomainDrilldown,
  PostStationRow
} from './types'

const DISPLAY_FALLBACK = '—'
const HOUR_FALLBACK = '--:--'

const COMPLIANCE_FROM_API: Record<PostStationDomainStatus, ComplianceState> = {
  OK: 'ok',
  FAIL: 'fail',
  PENDING: 'pending',
  NOT_REQUIRED: 'not_required'
}

// Identidade em valor (o enum da UI usa os valores da API — ver types.ts);
// o Record garante em compile-time a cobertura dos 5 status.
export const STATUS_FROM_API: Record<PostStationConsolidatedStatus, PostStationStatus> = {
  UNDER_VALIDATION: PostStationStatus.IN_EVALUATION,
  APPROVED: PostStationStatus.APPROVED,
  REVIEW: PostStationStatus.REVIEW,
  UNDER_ANALYSIS: PostStationStatus.IN_ANALYSIS,
  REVERTED: PostStationStatus.REVERTED
}

/**
 * RN-07 / RF-12: plantão com avaliação de Pós-Plantão Aprovada tem os dados
 * críticos travados para edição/realocação — o ajuste exige reverter a
 * validação primeiro. Extraída como função pura (em vez de repetir a
 * comparação com `STATUS_FROM_API` no `stationDetailsModal` e no
 * `update.station.modal`) para poder ser testada sem montar os componentes.
 *
 * `null`/`undefined` = sem avaliação (404) ou ainda não carregada ⇒ não trava
 * — a barreira real, nesse caso, é o backend.
 */
export const isEvaluationApproved = (
  evaluation: Pick<PostStationEvaluationItem, 'consolidatedStatus'> | null | undefined
): boolean =>
  evaluation != null &&
  STATUS_FROM_API[evaluation.consolidatedStatus] === PostStationStatus.APPROVED

// Dia/mês e hora saem de aritmética de offset (`src/utils/date.ts`), NÃO da
// opção `timeZone` do `Intl`: o backend persiste `stations.timezone` como offset
// ISO (`TIMEZONES.BRASILIA = '-03:00'`, default da coluna) e o `Intl` só aceita
// nome IANA — passar o offset cru lançava `RangeError` e derrubava o mapper
// inteiro. Nome IANA (fallback histórico e fixtures) continua aceito, caindo no
// offset fixo de Brasília.
const formatDayMonth = (iso: string, timezone: string): string =>
  formatDayMonthInOffset(iso, timezone) ?? DISPLAY_FALLBACK

const formatHour = (iso: string | null, timezone: string): string => {
  if (!iso) return HOUR_FALLBACK
  return formatHourMinuteInOffset(iso, timezone) ?? HOUR_FALLBACK
}

const formatCrm = (crm: string | null, crmUf: string | null): string => {
  if (!crm) return ''
  return crmUf ? `${crm}-${crmUf}` : crm
}

const mapCompliance = (domains: PostStationDomainStatuses): PostStationCompliance => ({
  registration: COMPLIANCE_FROM_API[domains.registration],
  qualification: COMPLIANCE_FROM_API[domains.qualificationDocumental],
  repass: COMPLIANCE_FROM_API[domains.qualificationTransfer],
  exception: COMPLIANCE_FROM_API[domains.exception],
  payment: COMPLIANCE_FROM_API[domains.payment]
})

// `criteria: []` de propósito — os critérios reais vêm do drill-down por domínio (`getDomainDetail`).
const buildDetailDomains = (compliance: PostStationCompliance): PostStationDomainDetail[] =>
  (Object.keys(DOMAIN_LABELS) as Array<keyof PostStationCompliance>).map((key) => ({
    key,
    label: DOMAIN_LABELS[key],
    state: compliance[key],
    criteria: []
  }))

// Relógio local do usuário: a varredura é da plataforma, não de um plantão — não
// há timezone de contexto. Null quando inválido (o caller decide o fallback).
export const formatScanDateTime = (iso: string | null): string | null => {
  if (!iso) return null
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return null
  const day = date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
  const hour = date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  return `${day} às ${hour}`
}

type DoctorIdentity = Pick<PostStationDomainDetailDoctor, 'name' | 'crm' | 'crmUf' | 'avatarUrl'>

const mapDoctorIdentity = (doctor: DoctorIdentity | null | undefined): PostStationDoctor => ({
  name: doctor?.name ?? DISPLAY_FALLBACK,
  crm: formatCrm(doctor?.crm ?? null, doctor?.crmUf ?? null),
  avatarUrl: doctor?.avatarUrl ?? null
})

export const mapEvaluationToRow = (item: PostStationEvaluationItem): PostStationRow => {
  // Plantão sem fuso persistido: string vazia em vez de um default local — o
  // fuso padrão do produto (Brasília) mora num só lugar, em `src/utils/date.ts`.
  // Mesmo padrão do callout de reversão (`PostStationRevertedCallout.vue`).
  const timezone = item.timezone || ''
  const compliance = mapCompliance(item.domains)
  const unit = item.healthUnitName ?? DISPLAY_FALLBACK
  const schedule = item.scheduleName ?? DISPLAY_FALLBACK

  return {
    id: item.id,
    evaluationId: item.id,
    doctorStationId: item.doctorStationId,
    stationId: item.stationId,
    scheduleId: item.scheduleId,
    doctor: mapDoctorIdentity(item.doctor),
    unit,
    scheduleLabel: schedule,
    date: formatDayMonth(item.date, timezone),
    dateIso: item.date,
    timeRange: `${formatHour(item.startHour, timezone)} → ${formatHour(item.endHour, timezone)}`,
    compliance,
    status: STATUS_FROM_API[item.consolidatedStatus],
    // F4 — o grid do backend (T9) passou a servir o estado do horário efetivo.
    // Lido por cast porque `PostStationEvaluationItem` mora num arquivo fora
    // desta task; `null` num backend anterior mantém a coluna como hoje.
    effectiveRegistrationStatus:
      (item as PostStationEvaluationItem & { effectiveRegistrationStatus?: string | null })
        .effectiveRegistrationStatus ?? null,
    // F4 (T9) — sem política vigente na data, o `NONE` acima não é "faltou
    // registro", é "ninguém avaliou". Lido por cast pelo mesmo motivo do campo
    // acima; `null` num backend anterior mantém a coluna como hoje.
    registrationPolicyInEffect:
      (item as PostStationEvaluationItem & { registrationPolicyInEffect?: boolean | null })
        .registrationPolicyInEffect ?? null,
    detail: {
      // A API do grid não fornece formation/team/position; a sidebar preenche
      // com o drill-down do domínio quando ele carrega.
      formation: '',
      team: '',
      position: '',
      shift: item.shiftLabel ?? DISPLAY_FALLBACK,
      dutyLabel: 'Plantão',
      company: item.companyName ?? DISPLAY_FALLBACK,
      unit,
      schedule,
      domains: buildDetailDomains(compliance)
    }
  }
}

const buildCriterionDescription = (
  expected: string | null | undefined,
  actual: string | null | undefined,
  // F4 (T7) — a fonte que VENCEU o ranking daquela perna. Entra por último e só
  // quando existe: perna não resolvida não ganha "fonte vencedora: —".
  sourceLabel?: string | null
): string | undefined => {
  const parts: string[] = []
  if (expected) parts.push(`Esperado ${expected}`)
  if (actual) parts.push(`Aplicado ${actual}`)
  if (sourceLabel) parts.push(`fonte vencedora: ${sourceLabel}`)
  return parts.length ? parts.join(' · ') : undefined
}

/**
 * Campos que a F4 acrescentou ao DTO do drill-down (T7 nos critérios, T8 no
 * topo). Declarados aqui, e não em `PostStationResponses.ts`, porque esse
 * arquivo é o espelho do contrato e está fora do recorte desta task — o cast
 * local mantém a leitura tolerante a um backend anterior à F4.
 */
type DomainDetailF4 = PostStationDomainDetailResponse & { registrationBlocking?: boolean }
type DomainCriterionF4 = PostStationDomainDetailResponse['criteria'][number] & {
  sourceKind?: string | null
  sourceLabel?: string | null
}

export const mapDomainDetail = (
  detail: PostStationDomainDetailResponse
): PostStationDomainDrilldown => ({
  registrationBlocking: (detail as DomainDetailF4).registrationBlocking,
  doctor: mapDoctorIdentity(detail.doctor),
  formation: detail.doctor?.formation ?? null,
  team: detail.doctor?.team ?? null,
  position: detail.doctor?.position ?? null,
  shift: detail.doctor?.shift ?? null,
  company: detail.companyName ?? DISPLAY_FALLBACK,
  unit: detail.healthUnitName ?? DISPLAY_FALLBACK,
  schedule: detail.scheduleName ?? DISPLAY_FALLBACK,
  state: COMPLIANCE_FROM_API[detail.domainStatus],
  criteria: detail.criteria.map((criterion) => {
    const { sourceKind, sourceLabel } = criterion as DomainCriterionF4
    return {
      key: criterion.key,
      label: criterion.label,
      description: buildCriterionDescription(criterion.expected, criterion.actual, sourceLabel),
      passed: criterion.result,
      actual: criterion.actual ?? undefined,
      // Spread condicional: critério sem os campos da F4 (os outros quatro
      // domínios, e todo backend anterior) sai com a MESMA forma de antes.
      ...(sourceKind !== undefined ? { sourceKind } : {}),
      ...(sourceLabel !== undefined ? { sourceLabel } : {})
    }
  }),
  origin: detail.origin
})
